<template>
  <div class="main">
    <!-- 查询区 -->
    <div class="query-item">
      <div class="search-l">
        <div style="margin-right: 30px;">
          规则名称
          <a-input class="inp ml10" placeholder="请输入" v-model:value="query.ruleName"></a-input>
        </div>
        <div style="margin-right: 30px;">
          规则类型
          <a-select class="inp ml10" placeholder="请选择" v-model:value="query.ruleType">
            <a-select-option value="">全部</a-select-option>
            <a-select-option value="1">按星期循环</a-select-option>
            <a-select-option value="2">按具体日期</a-select-option>
          </a-select>
        </div>
        <div style="margin-right: 30px;">
          状态
          <a-select class="inp ml10" placeholder="请选择" v-model:value="query.status">
            <a-select-option value="">全部</a-select-option>
            <a-select-option value="1">启用</a-select-option>
            <a-select-option value="0">禁用</a-select-option>
          </a-select>
        </div>
        <a-button class="ml30" @click="search" type="primary" ghost>查询</a-button>
        <a-button class="ml24" @click="reset" type="primary" ghost>重置</a-button>
        <a-button class="ml24" @click="openAddModal" type="primary">添加规则</a-button>
      </div>
    </div>

    <!-- 列表 -->
    <a-table
      :columns="columns"
      :row-key="(record) => record.id"
      :data-source="dataSource"
      size="middle"
      class="mt23"
      :pagination="pagination"
      :scroll="{ x: 1500, y: 'auto' }"
      v-autoHeight="{ extraHeight: 88, trHeight: 47 }"
    >
      <template #bodyCell="{ column, record, index }">
        <template v-if="column.key === 'index'">
          {{ (pagination.current - 1) * pagination.pageSize + index + 1 }}
        </template>
        <template v-if="column.key === 'ruleType'">
          {{ getRuleTypeText(record.ruleType) }}
        </template>
        <template v-if="column.key === 'dateInfo'">
          {{ getDateInfoText(record) }}
        </template>
        <template v-if="column.key === 'timeSlots'">
          {{ getTimeSlotsText(record.timeSlots) }}
        </template>
        <template v-if="column.key === 'status'">
          <a-switch
            :checked="record.status === 1"
            @change="(checked) => handleStatusChange(record, checked)"
          />
        </template>
        <template v-if="column.key === 'action'">
          <span>
            <a-button type="link" @click="handleEdit(record)" style="padding: 0">修改</a-button>
            <a-button class="ml10" type="link" danger @click="handleDelete(record)" style="padding: 0">删除</a-button>
          </span>
        </template>
      </template>
    </a-table>

    <!-- 添加/修改规则弹框 -->
    <a-modal
      v-model:open="modalVisible"
      :title="editingRule.id ? '修改规则' : '添加规则'"
      :width="640"
      @ok="handleSubmit"
      :confirm-loading="submitLoading"
    >
      <a-form :model="editingRule" layout="vertical">
        <a-form-item label="规则名称" required>
          <a-input v-model:value="editingRule.ruleName" placeholder="如：工作日早晚高峰禁行" />
        </a-form-item>
        <a-form-item label="规则类型" required>
          <a-radio-group v-model:value="editingRule.ruleType">
            <a-radio :value="1">按星期循环</a-radio>
            <a-radio :value="2">按具体日期</a-radio>
          </a-radio-group>
        </a-form-item>
        <a-form-item v-if="editingRule.ruleType === 1" label="适用星期" required>
          <a-checkbox-group v-model:value="editingRule.weekDaysArr">
            <a-checkbox :value="1">周一</a-checkbox>
            <a-checkbox :value="2">周二</a-checkbox>
            <a-checkbox :value="3">周三</a-checkbox>
            <a-checkbox :value="4">周四</a-checkbox>
            <a-checkbox :value="5">周五</a-checkbox>
            <a-checkbox :value="6">周六</a-checkbox>
            <a-checkbox :value="7">周日</a-checkbox>
          </a-checkbox-group>
        </a-form-item>
        <a-form-item v-if="editingRule.ruleType === 2" label="日期范围" required>
          <a-range-picker
            v-model:value="editingRule.dateRange"
            format="YYYY-MM-DD"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="禁行时段" required>
          <div v-for="(slot, idx) in editingRule.timeSlots" :key="idx" class="time-slot-row">
            <a-time-picker
              v-model:value="slot.start"
              format="HH:mm"
              placeholder="开始时间"
              :allow-clear="false"
            />
            <span class="time-separator">至</span>
            <a-time-picker
              v-model:value="slot.end"
              format="HH:mm"
              placeholder="结束时间"
              :allow-clear="false"
            />
            <a-button
              v-if="editingRule.timeSlots.length > 1"
              type="link"
              danger
              @click="removeTimeSlot(idx)"
            >删除</a-button>
          </div>
          <a-button type="dashed" block @click="addTimeSlot" style="margin-top: 8px;">
            + 添加时段
          </a-button>
        </a-form-item>
        <a-form-item label="状态">
          <a-switch v-model:checked="editingRule.statusBool" />
          <span class="ml10">{{ editingRule.statusBool ? '启用' : '禁用' }}</span>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { Modal, message } from 'ant-design-vue';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getPageList, create, update, deleteRule } from '@/api/roads/restrictionTimeRule.js';

dayjs.extend(customParseFormat);

const columns = [
  { title: '序号', key: 'index', width: 80, align: 'center' },
  { title: '规则名称', dataIndex: 'ruleName', key: 'ruleName', width: 300, align: 'center' },
  { title: '规则类型', key: 'ruleType', width: 120, align: 'center' },
  { title: '适用日期', key: 'dateInfo', width: 500, align: 'center' },
  { title: '禁行时段', key: 'timeSlots', width: 120, align: 'center' },
  { title: '状态', key: 'status', width: 80, align: 'center' },
  { title: '创建时间', key: 'createTime', width: 180, align: 'center' },
  { title: '操作', key: 'action', width: 140, align: 'center', fixed: 'right' },
];

let dataSource = ref([]);

let query = reactive({
  ruleName: '',
  ruleType: '',
  status: '',
});

// 弹框相关
const modalVisible = ref(false);
const submitLoading = ref(false);
const editingRule = reactive({
  id: null,
  ruleName: '',
  ruleType: 1,
  weekDaysArr: [],
  dateRange: null,
  timeSlots: [],
  statusBool: true,
});

const resetEditingRule = () => {
  editingRule.id = null;
  editingRule.ruleName = '';
  editingRule.ruleType = 1;
  editingRule.weekDaysArr = [];
  editingRule.dateRange = null;
  editingRule.timeSlots = [{ start: null, end: null }];
  editingRule.statusBool = true;
};

const openAddModal = () => {
  resetEditingRule();
  modalVisible.value = true;
};

const handleEdit = (record) => {
  editingRule.id = record.id;
  editingRule.ruleName = record.ruleName;
  editingRule.ruleType = record.ruleType;
  editingRule.statusBool = record.status === 1;

  if (record.ruleType === 1) {
    editingRule.weekDaysArr = record.weekDays
      ? record.weekDays.split(',').map(Number)
      : [];
    editingRule.dateRange = null;
  } else {
    editingRule.weekDaysArr = [];
    editingRule.dateRange = (record.startDate && record.endDate)
      ? [dayjs(record.startDate, 'YYYY-MM-DD'), dayjs(record.endDate, 'YYYY-MM-DD')]
      : null;
  }

  // 解析 timeSlots
  let slots = record.timeSlots;
  if (typeof slots === 'string') {
    try { slots = JSON.parse(slots); } catch (e) { slots = []; }
  }
  if (!Array.isArray(slots) || slots.length === 0) {
    slots = [{ start: '00:00', end: '23:59' }];
  }
  editingRule.timeSlots = slots.map((s) => ({
    start: s.start ? dayjs(s.start, 'HH:mm') : null,
    end: s.end ? dayjs(s.end, 'HH:mm') : null,
  }));

  modalVisible.value = true;
};

const addTimeSlot = () => {
  editingRule.timeSlots.push({ start: null, end: null });
};

const removeTimeSlot = (idx) => {
  editingRule.timeSlots.splice(idx, 1);
};

const handleSubmit = async () => {
  if (!editingRule.ruleName) {
    message.error('请填写规则名称');
    return;
  }
  if (editingRule.ruleType === 1 && editingRule.weekDaysArr.length === 0) {
    message.error('请选择适用星期');
    return;
  }
  if (editingRule.ruleType === 2 && !editingRule.dateRange) {
    message.error('请选择日期范围');
    return;
  }
  if (editingRule.timeSlots.length === 0) {
    message.error('请至少添加一个禁行时段');
    return;
  }
  for (const slot of editingRule.timeSlots) {
    if (!slot.start || !slot.end) {
      message.error('请填写完整的禁行时段');
      return;
    }
  }

  submitLoading.value = true;
  try {
    const payload = {
      id: editingRule.id,
      ruleName: editingRule.ruleName,
      ruleType: editingRule.ruleType,
      status: editingRule.statusBool ? 1 : 0,
      timeSlots: editingRule.timeSlots.map((s) => ({
        start: s.start.format('HH:mm'),
        end: s.end.format('HH:mm'),
      })),
    };

    if (editingRule.ruleType === 1) {
      payload.weekDays = editingRule.weekDaysArr.sort((a, b) => a - b).join(',');
      payload.startDate = null;
      payload.endDate = null;
    } else {
      payload.weekDays = null;
      payload.startDate = editingRule.dateRange[0].format('YYYY-MM-DD');
      payload.endDate = editingRule.dateRange[1].format('YYYY-MM-DD');
    }

    const res = editingRule.id ? await update(payload) : await create(payload);
    if (res.code === 200) {
      message.success(editingRule.id ? '修改成功' : '添加成功');
      modalVisible.value = false;
      getData();
    }
  } catch (error) {
    console.error('保存规则失败:', error);
    message.error('保存规则失败');
  } finally {
    submitLoading.value = false;
  }
};

const handleStatusChange = async (record, checked) => {
  const newStatus = checked ? 1 : 0;
  try {
    const res = await update({ ...record, status: newStatus });
    if (res.code === 200) {
      message.success(checked ? '已启用' : '已禁用');
      getData();
    }
  } catch (error) {
    console.error('状态更新失败:', error);
    message.error('状态更新失败');
  }
};

const handleDelete = (record) => {
  Modal.confirm({
    title: '确认删除',
    content: `确定要删除规则「${record.ruleName}」吗？`,
    okText: '确认',
    cancelText: '取消',
    okType: 'danger',
    onOk: async () => {
      try {
        const res = await deleteRule({ id: record.id });
        if (res.code === 200) {
          message.success('删除成功');
          getData();
        }
      } catch (error) {
        console.error('删除规则失败:', error);
        message.error('删除规则失败');
      }
    },
  });
};

// ========== 显示辅助 ==========
const getRuleTypeText = (type) => {
  return { 1: '按星期循环', 2: '按具体日期' }[type] || '-';
};

const weekDayText = (d) => ({
  1: '周一', 2: '周二', 3: '周三', 4: '周四',
  5: '周五', 6: '周六', 7: '周日',
}[d]);

const getDateInfoText = (record) => {
  if (record.ruleType === 1) {
    if (!record.weekDays) return '-';
    return record.weekDays.split(',').map(Number).map(weekDayText).join('、');
  }
  if (record.ruleType === 2) {
    if (!record.startDate || !record.endDate) return '-';
    return `${record.startDate} ~ ${record.endDate}`;
  }
  return '-';
};

const getTimeSlotsText = (timeSlots) => {
  let slots = timeSlots;
  if (typeof slots === 'string') {
    try { slots = JSON.parse(slots); } catch (e) { return '-'; }
  }
  if (!Array.isArray(slots) || slots.length === 0) return '-';
  return slots.map((s) => `${s.start}-${s.end}`).join('， ');
};

// ========== 分页 ==========
const pagination = reactive({
  total: 0,
  size: 'middle',
  defaultPageSize: 10,
  current: 1,
  pageSize: 10,
  showSizeChanger: true,
  showTotal: (total) => `共有 ${total} 条数据`,
  onShowSizeChange: (current, pageSize) => (pagination.pageSize = pageSize),
  onChange: pageChange,
  showSizeChange: pageSizeChange,
});

function pageChange(page, pageSize) {
  pagination.current = page;
  getData();
}

function pageSizeChange(current, size) {
  pagination.pageSize = size;
  getData();
}

const search = () => {
  pagination.current = 1;
  getData();
};

const reset = () => {
  query.ruleName = '';
  query.ruleType = '';
  query.status = '';
  getData();
};

// ========== 数据获取 ==========
const getData = async () => {
  try {
    const data = {
      pageIndex: pagination.current,
      pageSize: pagination.pageSize,
      conditionMap: {
        ruleName: query.ruleName,
        ruleType: query.ruleType,
        status: query.status,
      },
    };
    const res = await getPageList(data);
    if (res.code === 200) {
      dataSource.value = res.data.records || [];
      pagination.total = res.data.total || 0;
    }
  } catch (error) {
    console.error('获取规则列表失败:', error);
    message.error('获取规则列表失败');
  }
};

onMounted(async () => {
  await getData();
});
</script>

<style lang="less" scoped>
.inp {
  width: 13.5vw;
}

.query-item {
  display: flex;
  justify-content: space-between;

  .search-l {
    display: flex;
    flex-wrap: wrap;
  }
}

.main {
  padding: 20px;
  height: calc(100vh - 70px - 57px);
  background-color: #ffffff;
}

.time-slot-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  .time-separator {
    margin: 0 4px;
  }
}

/deep/ .ant-table-wrapper .ant-table-thead > tr > th,
.ant-table-wrapper .ant-table-thead > tr > td {
  font-size: 14px;
  font-family: PingFang SC-Regular, PingFang SC;
  font-weight: 400;
  color: #0A212E;
  background-color: #EFEFF5;
}

/deep/ .ant-table-wrapper .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr {
  color: rgba(104, 118, 126, 1);
}

/deep/ .ant-table-wrapper .ant-table-thead > tr > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before,
.ant-table-wrapper .ant-table-thead > tr > td:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
  width: 0px;
}
</style>
